import React, {useEffect, useMemo, useState} from 'react';
import {Alert, Box, Typography} from '@mui/material';
import {useDispatch, useSelector} from 'react-redux';
import {t} from 'i18next';
import './SourceSelectionStep.scss';
import ArrRadio from '../../../../../common/arr-radio/ArrRadio';
import {ReactComponent as DocumentIcon} from '../../../../../../assets/images/document-icon.svg';
import ArrSingleSelectDropdown from '../../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import ArrTreeView from '../../../../../common/arr-tree-view/ArrTreeView';
import {ISelectItem} from '../../../../../../shared/types';
import {RootState} from '../../../../../../redux/store';
import {AI_REVIEW_JOB_TYPE} from '../../../constant/AiReviewConstants';
import {ListItemsExtended, treeDataSourceSelection} from './utils';
import {
  useGetFoldersList,
  useGetTagsList,
} from '../../../../../../hooks/tagsFoldering/useTagsFoldering';
import ArrLoadingSpinner from '../../../../../common/arr-loading-spinner/ArrLoadingSpinner';
import {setFormData} from '../../../../../../redux/slice/aiReviewSlice';
export type SourceSelectionMode = 'SAVEDSEARCH' | 'TAG' | 'FOLDER';
import {useGetSavedSearchList} from '../../../../../../hooks/ai/investigate/useInvestigate';
import {ExtendedTreeItemProps} from '../../../../../../shared/interfaces';
import {TreeViewBaseItem} from '@mui/x-tree-view';

type SourceSelectionStepProps = {
  onSelectionChange?: (ids: string[]) => void;
};

const SourceSelectionStep: React.FC<SourceSelectionStepProps> = ({
  onSelectionChange,
}) => {
  const userSecurityGroupId = useSelector(
    (state: RootState) => state.authenticatedUser.user.securityGroupId
  );
  const {
    sourceSelectionMode: selectionMode,
    sourceSelection,
    groundTruth,
  } = useSelector((state: RootState) => state.aiReview.formData);
  const jobType = useSelector((state: RootState) => state.aiReview.jobType);
  const [selectedTag, setSelectedTag] = useState<ISelectItem | null>(null);
  const [selectedTreeItem, setSelectedTreeItem] = useState<string[] | null>(
    null
  );
  const [treeData, setTreeData] = useState<ListItemsExtended[]>([]);
  const [isTreeLoading, setIsTreeLoading] = useState(false);
  const dispatch = useDispatch();

  const hasRightColumn = jobType === AI_REVIEW_JOB_TYPE.relevancy;

  const groundTruthTagOptions: ISelectItem[] = useMemo(
    () => [
      {
        id: 'responsive',
        name: t('ai.review.sourceSelectionStep.responsive'),
      },
      {
        id: 'responsiveGroundTruth',
        name: t('ai.review.sourceSelectionStep.responsiveGroundTruth'),
      },
      {
        id: 'smeCodedResponsiveness',
        name: t('ai.review.sourceSelectionStep.sme_coded'),
      },
    ],
    []
  );

  const {
    mutate: fetchSavedSearchList,
    data: savedSearchList,
    isPending: isLoadingSavedSearchList,
  } = useGetSavedSearchList();

  const {
    data: foldersList,
    isLoading: isLoadingFoldersList,
    refetch: refetchGetFolderList,
  } = useGetFoldersList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=1`,
    false,
    0
  );

  const {
    data: tagsList,
    isLoading: isLoadingTagsList,
    refetch: refetchGetTagList,
  } = useGetTagsList(
    `?securityGroupId=${userSecurityGroupId}&toggelValue=1`,
    false,
    0
  );

  useEffect(() => {
    if (sourceSelection && sourceSelection !== 0) {
      setSelectedTreeItem([String(sourceSelection)]);
    }
    if (groundTruth) {
      const savedTag = groundTruthTagOptions.find(
        (opt) => opt.id === groundTruth
      );
      if (savedTag) setSelectedTag(savedTag);
    }
  }, []);

  const radioSelectionApiMapping = useMemo(() => {
    return {
      SAVEDSEARCH: {data: savedSearchList, fetch: fetchSavedSearchList},
      TAG: {data: tagsList?.tagList, fetch: refetchGetTagList},
      FOLDER: {data: foldersList, fetch: refetchGetFolderList},
    };
  }, [savedSearchList, tagsList, foldersList]);

  useEffect(() => {
    if (!radioSelectionApiMapping[selectionMode]?.data) {
      if (selectionMode === 'SAVEDSEARCH')
        radioSelectionApiMapping[selectionMode]?.fetch(true);
      else radioSelectionApiMapping[selectionMode]?.fetch();
    } else {
      const getTreeData = async () => {
        setIsTreeLoading(true);
        const roots: ListItemsExtended[] = await new Promise((resolve) => {
          setTimeout(() => {
            const treeResult = treeDataSourceSelection(
              radioSelectionApiMapping[selectionMode]?.data
            );
            resolve(treeResult);
          }, 0);
        });
        setTreeData(roots);
        setIsTreeLoading(false);
      };

      getTreeData();
    }
  }, [selectionMode, savedSearchList, tagsList, foldersList]);

  type TreeNode = (typeof treeData)[number];

  //Added the code to expand the tree on Load.
  // For now commnenting the code because it is iterating for each node, and if we have 1000s of row then screen gets hanged
  // will try new solution for this
  // const getAllIds = (
  //   nodes: TreeViewBaseItem<ExtendedTreeItemProps>[]
  // ): string[] => {
  //   const result: string[] = [];
  //   const dfs = (n: TreeViewBaseItem<ExtendedTreeItemProps>) => {
  //     result.push(n.id);
  //     n.children?.forEach(dfs);
  //   };
  //   nodes.forEach(dfs);
  //   return result;
  // };

  const findNodeById = (nodes: TreeNode[], id: string): TreeNode | null => {
    for (const node of nodes) {
      if (String(node.id) === id) return node;
      if (node.children && node.children.length > 0) {
        const found = findNodeById(node.children as TreeNode[], id);
        if (found) return found;
      }
    }
    return null;
  };

  const totalDocs = useMemo(() => {
    return selectedTreeItem
      ? (findNodeById(treeData, selectedTreeItem[0])?.documentCount ?? '---')
      : '---';
  }, [selectedTreeItem, treeData]);

  useEffect(() => {
    onSelectionChange?.(Number(totalDocs) > 0 ? (selectedTreeItem ?? []) : []);
  }, [totalDocs]);

  const selectMode = (mode: SourceSelectionMode) => {
    if (selectionMode !== mode) {
      dispatch(
        setFormData({
          sourceSelectionMode: mode,
          sourceSelection: 0,
        })
      );
      setSelectedTreeItem(null);
      onSelectionChange?.([]);
    }
  };

  return (
    <Box
      className="source-selection"
      component="section"
      role="region"
      aria-labelledby="source-selection-heading"
      data-testid="source-selection-container"
    >
      <Box
        className={`source-selection__column ${
          !hasRightColumn ? 'source-selection__column--single' : ''
        }`}
        data-testid="source-selection-column-grouping"
      >
        <Box
          className="source-selection__mode-bar"
          role="radiogroup"
          aria-label={t('ai.review.sourceSelectionStep.sourceSelectionMode')}
          data-testid="source-selection-mode-radiogroup"
        >
          {(['SAVEDSEARCH', 'TAG', 'FOLDER'] as SourceSelectionMode[]).map(
            (mode) => {
              const checked = selectionMode === mode;
              return (
                <Box
                  key={mode}
                  role="radio"
                  aria-checked={checked}
                  tabIndex={0}
                  className={`source-selection__mode-card ${
                    checked ? 'is-selected' : ''
                  }`}
                  onClick={() => {
                    if (selectionMode !== mode) {
                      dispatch(
                        setFormData({
                          sourceSelectionMode: mode,
                          sourceSelection: 0,
                        })
                      );
                      setSelectedTreeItem(null);
                      onSelectionChange?.([]);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === ' ' || e.key === 'Enter') {
                      e.preventDefault();
                      selectMode(mode);
                    }
                  }}
                  data-testid={`source-selection-mode-${mode}`}
                  aria-label={
                    mode === 'SAVEDSEARCH'
                      ? t('ai.review.sourceSelectionStep.selectSearches')
                      : mode === 'TAG'
                        ? t('ai.review.sourceSelectionStep.selectTags')
                        : t('ai.review.sourceSelectionStep.selectFolders')
                  }
                >
                  <ArrRadio
                    name="sourceSelectionMode"
                    dataTestId="source-selection-mode"
                    value={selectionMode}
                    data={[
                      {
                        label:
                          mode === 'SAVEDSEARCH'
                            ? t('ai.review.sourceSelectionStep.selectSearches')
                            : mode === 'TAG'
                              ? t('ai.review.sourceSelectionStep.selectTags')
                              : t(
                                  'ai.review.sourceSelectionStep.selectFolders'
                                ),
                        value: mode,
                      },
                    ]}
                    onChange={(value) => {
                      const newMode = value as SourceSelectionMode;
                      dispatch(
                        setFormData({
                          sourceSelectionMode: newMode,
                          sourceSelection: 0,
                        })
                      );
                      setSelectedTreeItem(null);
                      onSelectionChange?.([]);
                    }}
                    tabIndex={-1} // IMPORTANT: prevent double focus
                    size="medium"
                  />
                </Box>
              );
            }
          )}
        </Box>

        <Box
          className="source-selection__total-docs-bar"
          role="text"
          tabIndex={0}
          aria-label={`Total Docs, ${totalDocs}`}
          data-testid="source-selection-step-total-docs"
        >
          <DocumentIcon
            className="source-selection__total-docs-icon"
            aria-hidden="true"
            focusable={false}
            tabIndex={-1}
          />
          <Typography
            className="typo-text subhead4_size source-selection__total-docs-label text-body-strong"
            data-testid={`source-selection-step-total-docs`}
          >
            {t('ai.review.sourceSelectionStep.totalDocs')}
          </Typography>
          <Typography
            className="source-selection__total-docs-separator typo-text subhead4_size"
            data-testid={`source-selection-step-total-docs-count`}
          >
            {totalDocs}
          </Typography>
        </Box>
        {Number(totalDocs) <= 0 && (
          <Alert
            severity="error"
            variant="outlined"
            className="zero-doc-error-alert"
            tabIndex={0}
            aria-label={t('ai.review.sourceSelectionStep.noDocErrorMsg')}
            data-testid={'source-selection-step-zero-doc-error'}
          >
            <Typography className="typo-text body3_size text-critical">
              {t('ai.review.sourceSelectionStep.noDocErrorMsg')}
            </Typography>
          </Alert>
        )}

        <Box
          className="source-selection__tree-view"
          aria-label={t('ai.review.sourceSelectionStep.documentsTreeView')}
          data-testid="source-selection-tree-view"
        >
          {isTreeLoading ||
          isLoadingSavedSearchList ||
          isLoadingFoldersList ||
          isLoadingTagsList ? (
            <Box className="tree-view-loader">
              <ArrLoadingSpinner />
            </Box>
          ) : (
            <ArrTreeView
              multipleSelection={false}
              selectedItem={
                selectedTreeItem && selectedTreeItem.length > 0
                  ? selectedTreeItem[0]
                  : null
              }
              setSelectedItem={(ids) => {
                setSelectedTreeItem(ids);
                dispatch(
                  setFormData({
                    sourceSelection: Number(ids?.[0] ?? 0),
                  })
                );
              }}
              itemsList={treeData}
              isShowCheckbox={true}
              isShowCountLabel={false}
              handleNoResultMessage={t('common.noResultFound')}
              expandOnSearch={false}
              showTooltip
              isBlockParentNodeSelection={true}
            />
          )}
        </Box>
      </Box>

      {hasRightColumn && (
        <Box className="source-selection__column source-selection__column--right">
          <Box
            className="source-selection__ground-truth-bar"
            tabIndex={0}
            data-testid={`source-selection-step-ground-truth-field-wrapper`}
            aria-label={`${t('ai.review.sourceSelectionStep.groundTruthField')} ${t('ai.review.sourceSelectionStep.optional')}`}
          >
            <Typography
              className="typo-text subhead4_size source-selection__ground-truth-label"
              data-testid={`source-selection-step-ground-truth-field`}
            >
              {t('ai.review.sourceSelectionStep.groundTruthField')}
            </Typography>
            &nbsp;&nbsp;
            <Typography
              className="text-body-subtle typo-text subhead4_size"
              data-testid={`source-selection-step-optional`}
            >
              {t('ai.review.sourceSelectionStep.optional')}
            </Typography>
          </Box>
          <Box
            className="source-selection__ground-truth-description"
            tabIndex={0}
            aria-label={t(
              'ai.review.sourceSelectionStep.groundTruthDescription'
            )}
          >
            <Typography
              className="typo-text body3_size source-selection__ground-truth-description-text"
              data-testid={`source-selection-step-ground-truth-description`}
            >
              {t('ai.review.sourceSelectionStep.groundTruthDescription')}
            </Typography>
          </Box>
          <Box
            className="source-selection__ground-truth-select"
            tabIndex={-1}
            data-testid="source-selection-drop-down"
          >
            <ArrSingleSelectDropdown
              dropdownLabel={t('ai.review.sourceSelectionStep.selectATag')}
              dropdownItems={groundTruthTagOptions}
              selectedValues={selectedTag}
              dropdownItemDisplay="name"
              dropdownItemKey="id"
              onSelectionChange={(value) => {
                setSelectedTag(value);
                dispatch(
                  setFormData({
                    groundTruth: value?.id as string,
                  })
                );
              }}
              dataTestId="asd-ground-truth-tag-select"
              sizeVariant="medium"
              tabIndex={-1}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default SourceSelectionStep;
